/**
 * New Year bingo cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearBingoCardsPack: ToilePack = {
    slug: 'new-year-bingo-cards',
    category: 'season',
    name: 'New Year bingo cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'pictures', label: 'Picture bingo', subject: 'A bingo card with a 5 by 5 grid of small party pictures, a clock in the centre free square, headed exactly "BINGO"'},
        {key: 'words', label: 'Word bingo', subject: 'A bingo card with a 5 by 5 grid of new year words, a centre square lettered exactly "FREE", a gold border'},
        {key: 'calling', label: 'Calling cards', subject: 'A sheet of small square calling cards each with one party picture and its name lettered exactly underneath, such as "CLOCK" or "CONFETTI", dashed cut lines, in a grid'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a dark page with a gold border and a margin'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a vertical page with a confetti border and a white margin'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a clean page with wide margins'},
    ],
    directions: ['with numbers instead of pictures', 'for a smaller group of six', 'as a countdown-clock variant'],
    directionHint: 'try: numbers instead of pictures',
    seo: {
        title: 'New Year Bingo Cards: Pictures, Words & Calling',
        metaDescription: 'Nine New Year bingo pieces — picture bingo, word bingo, and calling cards — in black-and-gold, confetti, and minimal styles, ready for the party.',
        h1: 'New Year bingo cards: picture bingo, word bingo, calling cards',
        tagline: 'Nine pieces, three bingo formats, three styles each — ready to print for the party.',
        intro: "Three pieces for a New Year's Eve bingo game: a picture bingo card, a word bingo card, and a sheet of calling cards, each in black-and-gold, confetti-party, and minimal styles. Ask for a different grid size or a themed calling deck, and the same bingo grammar carries it.",
        useCases: [
            "a New Year's Eve family game",
            'a classroom countdown party',
            'a community NYE event',
            'a virtual-party printable send-out',
        ],
        faq: [
            {q: 'Do I get one bingo card or several different ones?', a: 'Each render is one card design — print multiple copies, or generate a second layout, so every player has a different card.'},
            {q: 'What size do these print at?', a: 'Each card is a single vertical page, close to US letter or A4 proportions.'},
            {q: 'Can I get a different picture set or grid size?', a: 'Yes — the direction field takes any variation, including a themed picture set or a different grid size.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
